// src/components/MilestoneDetail.js — "how do I earn this badge?"
//
// Opens when a member taps a badge (Home shelf, Milestones page, Milestone
// Wall). Shows the badge large enough to read (the "12 WEEKS" line is ~5px at
// shelf size), whether it's earned, how it's earned in plain words, the
// member's progress, and fine print that states EXACTLY how the app counts
// (see isClassCompletion / computeMilestones in services/milestones.js — if
// those rules change, change the fine print defaults below too).
//
// READ-ONLY: renders values computeMilestones() already produced. No fetch,
// no writes, no effect on how any badge is earned.
//
// Computer: centred glass card, ← → to browse, Esc to close.
// Phone (≤600px): bottom sheet, swipe sideways to browse, swipe down to close.
// Portalled to <body> so no transformed/blurred ancestor can trap or clip it.
// Design: docs/ux/milestone-detail-preview.html.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MilestoneBadge from './MilestoneBadge';
import { BADGE_BY_KEY, bestRunNote } from '../services/milestones';

// ── Copy (admin-editable via siteConfig; these are the fallbacks) ─────────
const DEFAULTS = {
    milestoneTapHint: "Tap a badge to see how it's earned",
    milestoneHowLabel: 'How to earn it',
    milestoneNotEarnedLabel: 'Not yet earned',
    milestoneEarnedLabel: 'Earned',
    milestoneEarnedByTemplate: '{name} earned this',
    milestoneMetaFirst: 'Your very first class',
    milestoneMetaClassesTemplate: '{count} classes',
    milestoneMetaWeeksTemplate: '{count} weeks in a row',
    milestoneProgressClassesTemplate: '{current} of {target} classes',
    milestoneProgressWeeksTemplate: '{current} of {target} weeks in your current run',
    milestoneToGoTemplate: '{count} to go',
    milestoneYouLabel: 'You',
    milestoneYouEarnedLabel: "You've earned this one too",
    milestoneHow_first: 'Complete your first class.',
    milestoneHow_ten: 'Complete 10 classes.',
    milestoneHow_tfive: 'Complete 25 classes.',
    milestoneHow_fifty: 'Complete 50 classes.',
    milestoneHow_hundred: 'Complete 100 classes.',
    milestoneHow_quarter: 'Complete 250 classes.',
    milestoneHow_mo1: 'Take at least one class every week for 4 weeks in a row.',
    milestoneHow_mo3: 'Take at least one class every week for 12 weeks in a row.',
    milestoneHow_mo6: 'Take at least one class every week for 26 weeks in a row.',
    milestoneRuleClass1: 'Live classes and On Demand classes both count — and so does repeating a class you love.',
    milestoneRuleClass2: "A class counts once it's complete: automatically when you watch most of it, or when you tap Log this workout.",
    milestoneRuleClass3: "Shorts, program workouts and challenge days don't count toward class milestones.",
    milestoneRuleWeek1: 'Weeks run Sunday through Saturday.',
    milestoneRuleWeek2: 'A week counts once it ends — so your badge arrives the Sunday after your last week.',
    milestoneRuleWeek3: "Miss a week and your run starts again. Badges you've earned are always yours to keep.",
    // Seasonal — The Gift of Showing Up 2026
    // Owner's copy, verbatim (2026-11-01).
    milestoneHow_gift26: 'Complete 18 membership workouts between November 1 and December 31, 2026.',
    milestoneMetaSeasonalTemplate: '{count} workouts · Holiday Season 2026',
    milestoneProgressSeasonalTemplate: '{current} of {target} workouts this season',
    milestoneRuleSeason1: 'Live and On Demand membership classes both count, including repeats of your favorites.',
    milestoneRuleSeason2: 'Workouts count once completed, either automatically or when you log them in the app.',
    milestoneRuleSeason3: "You made time for yourself during one of the busiest seasons of the year. That's worth celebrating!",
};

export const MILESTONE_DETAIL_DEFAULTS = DEFAULTS;

function pick(c, key) {
    const v = c && c[key];
    return typeof v === 'string' && v.trim() ? v : DEFAULTS[key];
}

function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

function formatDay(dayKey) {
    if (!dayKey) return '';
    const [y, m, d] = String(dayKey).split('-').map(Number);
    if (!y || !m || !d) return '';
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    return dt.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        ...(dt.getUTCFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
        timeZone: 'UTC',
    });
}

function formatIso(iso) {
    if (!iso) return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        ...(dt.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });
}

// Production sets warm-gray == charcoal and cream-dark == cream, so secondary
// tones and hairlines are derived (reference_live_theme_values).
const T = {
    soft: 'color-mix(in srgb, var(--mom-charcoal) 70%, var(--mom-cream))',
    faint: 'color-mix(in srgb, var(--mom-charcoal) 48%, var(--mom-cream))',
    line: 'color-mix(in srgb, var(--mom-charcoal) 11%, var(--mom-cream))',
    track: 'color-mix(in srgb, var(--mom-charcoal) 8%, var(--mom-cream))',
    well: 'color-mix(in srgb, var(--mom-cream) 70%, #fff)',
};
const sans = 'var(--mom-font-sans)';
const serif = 'var(--mom-font-serif)';

// Reset for a badge wrapped in a <button> (shelf / page / wall), so it reads
// exactly as before but is tappable and keyboard-reachable.
export const badgeButtonReset = {
    background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer',
    font: 'inherit', color: 'inherit', textAlign: 'center', lineHeight: 'inherit',
    borderRadius: 16, WebkitTapHighlightColor: 'transparent',
};

function NavArrow({ dir, onClick, label }) {
    const [hover, setHover] = useState(false);
    return (
        <button type="button" aria-label={label} onClick={onClick}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                position: 'absolute', top: '50%', [dir === 'prev' ? 'left' : 'right']: -56,
                transform: `translateY(-50%)${hover ? ' scale(1.06)' : ''}`,
                width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.92)', color: 'var(--mom-charcoal)',
                boxShadow: hover ? '0 8px 22px rgba(62,47,47,0.18)' : '0 4px 14px rgba(62,47,47,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                {dir === 'prev' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
            </svg>
        </button>
    );
}

function CloseButton({ onClick }) {
    const [hover, setHover] = useState(false);
    return (
        <button type="button" aria-label="Close" onClick={onClick}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                position: 'absolute', top: 14, right: 14, zIndex: 2, width: 36, height: 36, borderRadius: '50%',
                border: 'none', cursor: 'pointer', padding: 0,
                background: hover ? 'rgba(62,47,47,0.10)' : 'rgba(62,47,47,0.06)', color: 'var(--mom-charcoal)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s',
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
    );
}

// The card's content for one badge. `mine` = the viewer's own status for this
// badge from computeMilestones (earned entry, unearned entry, or null when the
// viewer's milestones aren't available). `earnedBy` = someone else's Wall post.
function DetailBody({ badge, mine, earnedBy, copy, compact }) {
    const isWeekly = badge.family === 'consistency';
    const isSeasonal = badge.family === 'seasonal';
    // computeMilestones: earned entries carry earnedDayCT and NO progress;
    // unearned entries always carry a numeric progress.
    const unearnedMine = !!mine && typeof mine.progress === 'number';
    const earnedMine = !!mine && !unearnedMine;
    const showLocked = !earnedBy && !earnedMine;

    let kicker;
    if (earnedBy) {
        const date = formatIso(earnedBy.earnedAt);
        kicker = fill(pick(copy, 'milestoneEarnedByTemplate'), { name: earnedBy.name || 'A member' }) + (date ? ` · ${date}` : '');
    } else if (earnedMine) {
        const date = formatDay(mine.earnedDayCT);
        kicker = pick(copy, 'milestoneEarnedLabel') + (date ? ` · ${date}` : '');
    } else {
        kicker = pick(copy, 'milestoneNotEarnedLabel');
    }

    const meta = isSeasonal
        ? fill(pick(copy, 'milestoneMetaSeasonalTemplate'), { count: badge.threshold })
        : isWeekly
        ? fill(pick(copy, 'milestoneMetaWeeksTemplate'), { count: badge.threshold })
        : badge.threshold === 1
            ? pick(copy, 'milestoneMetaFirst')
            : fill(pick(copy, 'milestoneMetaClassesTemplate'), { count: badge.threshold });

    const current = unearnedMine ? Math.max(0, Number(mine.current) || 0) : 0;
    const progressText = unearnedMine
        ? fill(pick(copy, isSeasonal ? 'milestoneProgressSeasonalTemplate' : isWeekly ? 'milestoneProgressWeeksTemplate' : 'milestoneProgressClassesTemplate'), { current, target: badge.threshold })
        : '';
    const best = unearnedMine ? bestRunNote(mine, copy) : null;
    const rules = isSeasonal
        ? ['milestoneRuleSeason1', 'milestoneRuleSeason2', 'milestoneRuleSeason3']
        : isWeekly
        ? ['milestoneRuleWeek1', 'milestoneRuleWeek2', 'milestoneRuleWeek3']
        : ['milestoneRuleClass1', 'milestoneRuleClass2', 'milestoneRuleClass3'];

    const heroSize = compact ? 148 : 176;

    return (
        <div>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', margin: compact ? '0 0 10px' : '6px 0 14px' }}>
                <div aria-hidden="true" style={{
                    position: 'absolute', top: '50%', left: '50%', width: heroSize * 1.25, height: heroSize * 1.25,
                    transform: 'translate(-50%, -50%)', borderRadius: '50%', pointerEvents: 'none',
                    background: showLocked
                        ? 'radial-gradient(circle, color-mix(in srgb, var(--mom-charcoal) 6%, transparent), transparent 65%)'
                        : 'radial-gradient(circle, color-mix(in srgb, var(--mom-primary) 16%, transparent), transparent 65%)',
                }} />
                <div style={{ position: 'relative', animation: 'momBadgeIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both' }}>
                    <MilestoneBadge badgeKey={badge.key} size={heroSize} locked={showLocked}
                        progress={unearnedMine ? mine.progress : 0} />
                </div>
            </div>

            <p style={{
                textAlign: 'center', fontFamily: sans, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: showLocked ? T.faint : 'var(--mom-primary)', margin: 0,
            }}>{kicker}</p>
            <h2 id="mom-milestone-detail-title" style={{
                textAlign: 'center', fontFamily: serif, fontStyle: 'italic', fontWeight: 600,
                fontSize: compact ? 25 : 27, color: 'var(--mom-charcoal)', margin: '6px 0 2px', lineHeight: 1.2,
            }}>{badge.name}</h2>
            <p style={{ textAlign: 'center', fontFamily: sans, fontSize: 13, color: T.soft, margin: 0 }}>{meta}</p>

            <div style={{ marginTop: 20, padding: '15px 18px', borderRadius: 18, background: T.well, border: `1px solid ${T.line}` }}>
                <p style={{ fontFamily: sans, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.faint, margin: '0 0 6px' }}>
                    {pick(copy, 'milestoneHowLabel')}
                </p>
                <p style={{ fontFamily: serif, fontSize: 17, lineHeight: 1.45, color: 'var(--mom-charcoal)', margin: 0 }}>
                    {pick(copy, `milestoneHow_${badge.key}`)}
                </p>
            </div>

            {/* Your progress (own badge, not yet earned) */}
            {!earnedBy && unearnedMine && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ height: 7, borderRadius: 99, background: T.track, overflow: 'hidden' }}>
                        <div style={{
                            height: '100%', width: `${Math.round(Math.max(0, Math.min(1, mine.progress)) * 100)}%`,
                            borderRadius: 99, background: 'linear-gradient(90deg, var(--mom-primary), var(--mom-primary-light, #e48aa6))',
                            transition: 'width 0.6s ease',
                        }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 8, fontFamily: sans, fontSize: 12.5, color: T.soft }}>
                        <span><b style={{ color: 'var(--mom-charcoal)', fontWeight: 700 }}>{progressText}</b></span>
                        <span style={{ whiteSpace: 'nowrap' }}>{fill(pick(copy, 'milestoneToGoTemplate'), { count: Math.max(0, badge.threshold - current) })}</span>
                    </div>
                    {best && <p style={{ fontFamily: sans, fontSize: 12, color: T.soft, margin: '4px 0 0' }}>{best}</p>}
                </div>
            )}

            {/* Someone else's badge: a quiet line about the viewer */}
            {earnedBy && mine && (
                <p style={{ marginTop: 14, fontFamily: sans, fontSize: 12.5, color: T.soft, textAlign: 'center' }}>
                    {earnedMine
                        ? pick(copy, 'milestoneYouEarnedLabel')
                        : <>{pick(copy, 'milestoneYouLabel')}: <b style={{ color: 'var(--mom-charcoal)', fontWeight: 700 }}>{progressText}</b></>}
                </p>
            )}

            <div style={{ marginTop: 16, display: 'grid', gap: 9 }}>
                {rules.map((k) => (
                    <div key={k} style={{ display: 'flex', gap: 10, fontFamily: sans, fontSize: 13, lineHeight: 1.5, color: T.soft }}>
                        <span aria-hidden="true" style={{
                            flex: '0 0 18px', height: 18, borderRadius: '50%', marginTop: 1,
                            background: 'color-mix(in srgb, var(--mom-primary) 10%, #fff)', color: 'var(--mom-primary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </span>
                        <span>{pick(copy, k)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Find the viewer's own entry for a badge in computeMilestones() output.
function mineFor(milestones, key) {
    if (!milestones) return null;
    return (milestones.earned || []).find((b) => b.key === key)
        || (milestones.unearned || []).find((b) => b.key === key)
        || null;
}

function MilestoneDetail({ badgeKey, keys, milestones, copy, earnedBy, onNavigate, onClose }) {
    const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 600px)').matches);
    const [closing, setClosing] = useState(false);
    const dialogRef = useRef(null);
    const scrollRef = useRef(null);
    const gesture = useRef(null);
    const [dragY, setDragY] = useState(0);

    const list = Array.isArray(keys) && keys.length ? keys : [badgeKey];
    const idx = Math.max(0, list.indexOf(badgeKey));
    const canNav = list.length > 1 && !earnedBy;

    // Track the shown badge in a ref so rapid presses (a held arrow key) each
    // advance from the latest badge rather than the last rendered one.
    const currentRef = useRef(badgeKey);
    currentRef.current = badgeKey;
    const go = useCallback((delta) => {
        if (!canNav) return;
        const from = Math.max(0, list.indexOf(currentRef.current));
        const next = list[(from + delta + list.length) % list.length];
        currentRef.current = next;
        onNavigate?.(next);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [canNav, list, onNavigate]);

    const close = useCallback(() => {
        if (closing) return;
        setClosing(true);
        setTimeout(() => onClose?.(), 200);
    }, [closing, onClose]);

    useEffect(() => {
        const mq = window.matchMedia?.('(max-width: 600px)');
        if (!mq) return undefined;
        const fn = (e) => setIsPhone(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', fn); else mq.addListener(fn);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', fn); else mq.removeListener(fn); };
    }, []);

    // Focus in, restore focus out; lock page scroll while open.
    useEffect(() => {
        const prevFocus = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
            if (prevFocus && typeof prevFocus.focus === 'function') { try { prevFocus.focus(); } catch (e) { /* gone */ } }
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); close(); }
            else if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'ArrowRight') go(1);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [close, go]);

    const badge = BADGE_BY_KEY[badgeKey];
    if (!badge) return null;
    const mine = mineFor(milestones, badgeKey);

    // Phone gestures: sideways swipe browses; a downward drag that starts while
    // the sheet is scrolled to the top closes it. Vertical scrolling inside the
    // sheet is left to the browser (touch-action: pan-y).
    const onPointerDown = (e) => {
        if (!isPhone || e.pointerType === 'mouse') return;
        gesture.current = { x: e.clientX, y: e.clientY, atTop: !scrollRef.current || scrollRef.current.scrollTop <= 0, axis: null };
    };
    const onPointerMove = (e) => {
        const g = gesture.current;
        if (!g) return;
        const dx = e.clientX - g.x, dy = e.clientY - g.y;
        if (!g.axis && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (g.axis === 'y' && g.atTop && dy > 0) setDragY(dy);
    };
    const onPointerEnd = (e) => {
        const g = gesture.current;
        gesture.current = null;
        if (!g) return;
        const dx = e.clientX - g.x, dy = e.clientY - g.y;
        if (g.axis === 'x' && Math.abs(dx) > 56) go(dx < 0 ? 1 : -1);
        if (g.axis === 'y' && g.atTop && dy > 90) { close(); return; }
        setDragY(0);
    };

    const dots = canNav && (
        <div aria-hidden="true" style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
            {list.map((k, i) => (
                <span key={k} style={{
                    width: i === idx ? 18 : 6, height: 6, borderRadius: 99, transition: 'all 0.2s ease',
                    background: i === idx ? 'var(--mom-primary)' : 'color-mix(in srgb, var(--mom-charcoal) 16%, var(--mom-cream))',
                }} />
            ))}
        </div>
    );

    const body = (
        <div key={badgeKey} style={{ animation: 'momDetailSwap 0.25s ease both' }}>
            <DetailBody badge={badge} mine={mine} earnedBy={earnedBy} copy={copy} compact={isPhone} />
            {dots}
        </div>
    );

    return createPortal(
        <div
            onClick={close}
            style={{
                position: 'fixed', inset: 0, zIndex: 3150,
                display: 'flex', alignItems: isPhone ? 'flex-end' : 'center', justifyContent: 'center',
                padding: isPhone ? 0 : 24,
                background: isPhone ? 'rgba(62,47,47,0.28)' : 'rgba(250,245,241,0.55)',
                backdropFilter: 'blur(14px) saturate(1.2)', WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
                animation: `${closing ? 'momDetailFadeOut' : 'momDetailFadeIn'} 0.22s ease both`,
            }}
        >
            {/* Outer layer owns the enter/exit animation; the dialog inside owns
                the drag offset — an animation's fill would otherwise override an
                inline transform and the sheet couldn't follow the finger. */}
            <div style={{
                width: isPhone ? '100%' : 440, maxWidth: '100%',
                animation: closing
                    ? `${isPhone ? 'momSheetOut' : 'momCardOut'} 0.2s ease both`
                    : `${isPhone ? 'momSheetIn' : 'momCardIn'} 0.38s cubic-bezier(0.16, 1, 0.3, 1) both`,
            }}>
            <div
                ref={dialogRef}
                role="dialog" aria-modal="true" aria-labelledby="mom-milestone-detail-title"
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
                style={{
                    position: 'relative', outline: 'none',
                    width: '100%',
                    background: isPhone ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.7)',
                    borderRadius: isPhone ? '28px 28px 0 0' : 28,
                    boxShadow: isPhone ? '0 -12px 40px rgba(62,47,47,0.16)' : '0 32px 80px rgba(62,47,47,0.16), 0 8px 30px rgba(62,47,47,0.08)',
                    transform: dragY ? `translateY(${dragY}px)` : undefined,
                    transition: dragY ? 'none' : 'transform 0.25s ease',
                    touchAction: 'pan-y',
                }}
            >
                {!isPhone && canNav && <NavArrow dir="prev" label="Previous badge" onClick={() => go(-1)} />}
                {!isPhone && canNav && <NavArrow dir="next" label="Next badge" onClick={() => go(1)} />}
                <CloseButton onClick={close} />
                <div ref={scrollRef} style={{
                    maxHeight: isPhone ? '88vh' : 'min(86vh, 760px)', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
                    padding: isPhone ? '12px 22px calc(26px + env(safe-area-inset-bottom))' : '30px 30px 26px',
                    borderRadius: 'inherit',
                }}>
                    {isPhone && <div aria-hidden="true" style={{ width: 40, height: 5, borderRadius: 99, margin: '0 auto 12px', background: 'color-mix(in srgb, var(--mom-charcoal) 18%, var(--mom-cream))' }} />}
                    {body}
                </div>
            </div>
            </div>
            <style>{`
                @keyframes momDetailFadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes momDetailFadeOut { from { opacity: 1 } to { opacity: 0 } }
                @keyframes momCardIn { from { opacity: 0; transform: scale(0.94) translateY(14px) } to { opacity: 1; transform: none } }
                @keyframes momCardOut { from { opacity: 1; transform: none } to { opacity: 0; transform: scale(0.97) translateY(8px) } }
                @keyframes momSheetIn { from { transform: translateY(100%) } to { transform: none } }
                @keyframes momSheetOut { from { transform: none } to { transform: translateY(100%) } }
                @keyframes momDetailSwap { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
                @keyframes momBadgeIn { from { opacity: 0; transform: scale(0.86) } to { opacity: 1; transform: none } }
            `}</style>
        </div>,
        document.body,
    );
}

export default MilestoneDetail;
